import React, { useState, useRef, useMemo, useCallback } from 'react';

import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ColumnDef, SortingState } from '@tanstack/react-table';

import {
  LoanDatatableLoan_CRELoan_pools,
  LoanDatatableLoan_AutoLoan_pools,
  LoanDatatableLoan_HomeLoan_pools,
  LoanDatatableLoan_UnsecuredPersonalLoan_pools,
} from './__generated__/LoanDatatableLoan';
import { Popover, DataDisplayTable, WithSubData } from '../../../ui-kit';
import { POOL_PARAM } from 'app-level/navigation';

type Pool =
  | LoanDatatableLoan_CRELoan_pools
  | LoanDatatableLoan_AutoLoan_pools
  | LoanDatatableLoan_HomeLoan_pools
  | LoanDatatableLoan_UnsecuredPersonalLoan_pools;

interface PoolsColumnProps {
  pools:
    | LoanDatatableLoan_CRELoan_pools[]
    | LoanDatatableLoan_AutoLoan_pools[]
    | LoanDatatableLoan_HomeLoan_pools[]
    | LoanDatatableLoan_UnsecuredPersonalLoan_pools[]
    | null;
  assetClass: string;
}

const getAssetClassUrlSegment = (assetClass: string): string => {
  switch (assetClass) {
    case 'AUTO':
      return 'auto';
    case 'HOME':
      return 'home';
    case 'MBL':
      return 'mbl';
    case 'CRE':
      return 'cre';
    case 'UNSECPERSONAL':
      return 'unsecured-personal';
    default:
      return 'home'; // fallback
  }
};

const PoolsColumn: React.FC<PoolsColumnProps> = ({ pools, assetClass }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const getPoolUrl = useCallback(
    (pool: Pool) => {
      const poolId = encodeURIComponent(pool.id);
      const assetClassSegment = getAssetClassUrlSegment(pool.asset_class);
      const searchParams = new URLSearchParams(location.search);
      if (poolId) {
        searchParams.set(POOL_PARAM, poolId);
      } else {
        searchParams.delete(POOL_PARAM);
      }
      return `/portfolio/loan-level-data/${assetClassSegment}?${searchParams.toString()}`;
    },
    [location.search],
  );
  // Column definitions styled like the reference table
  const columns = useMemo(
    () => [
      {
        id: 'name',
        header: 'POOL NAME',
        accessorKey: 'name',
        meta: { headerNoPill: true },
        cell: ({ row }: any) => {
          const pool = row.original;
          const poolId = encodeURIComponent(pool.id);
          const assetClassSegment = getAssetClassUrlSegment(pool.asset_class);
          const searchParams = new URLSearchParams(location.search);
          if (poolId) {
            searchParams.set(POOL_PARAM, poolId);
          } else {
            searchParams.delete(POOL_PARAM);
          }

          const poolUrl = getPoolUrl(pool);
          return (
            <div className="px-4 py-1">
              <Link
                to={poolUrl}
                className="block max-w-48 truncate text-brand-500 hover:text-brand-600 hover:underline hover:decoration-brand-600"
                title={pool.name}
              >
                {pool.name}
              </Link>
            </div>
          );
        },
      },
      {
        id: 'asset_class',
        header: 'ASSET CLASS',
        accessorKey: 'asset_class',
        meta: { headerNoPill: true },
        cell: ({ getValue }: any) => (
          <div className="truncate px-4 py-1 text-white">{getValue()}</div>
        ),
      },
      {
        id: 'loan_count',
        header: '# LOANS',
        accessorKey: 'loan_count',
        enableSorting: true,
        meta: { headerNoPill: true },
        cell: ({ getValue }: any) => (
          <div className="px-4 py-3 font-medium text-yellow-400">{getValue() ?? 0}</div>
        ),
      },
    ],
    [location.search],
  );

  // Transform pools data to WithSubData format
  const tableData: WithSubData<Pool>[] = pools
    ? pools.map((pool) => ({
        ...pool,
        last_updated_date: '',
        __typename: 'Pool',
        subData: [],
      }))
    : [];

  // If no pools, show dash
  if (!pools || pools.length === 0) {
    return <span className="text-gray-500">-</span>;
  }

  // If only one pool, show it directly as a link (similar to existing listing behavior)
  if (pools.length === 1) {
    const pool = pools[0];

    const poolUrl = getPoolUrl(pool);

    return (
      <Link
        to={poolUrl}
        className="text-brand-500 hover:text-brand-600 hover:underline hover:decoration-brand-600"
      >
        {pool.name}
      </Link>
    );
  }

  // Multiple pools - show expandable list
  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1 text-brand-500 hover:text-brand-600 focus:outline-none"
      >
        <span className="font-mono text-xs">{isExpanded ? '▼' : '▶'}</span>
        <span className="text-sm font-medium">
          {pools.length} pool{pools.length > 1 ? 's' : ''}
        </span>
      </button>

      <Popover
        anchorRef={buttonRef}
        open={isExpanded}
        onClose={() => setIsExpanded(false)}
        position="down"
        headerText={
          <div className="text-s mb-2 font-semibold uppercase tracking-wide text-gray-500">
            {pools.length} Pools
          </div>
        }
        showClose={true}
        className="min-w-96 max-w-2xl"
      >
        <div className="max-h-64 overflow-y-auto">
          <DataDisplayTable
            data={tableData}
            columns={columns}
            noDataMessage="No pools found"
            sorting={{
              state: sorting,
              onSortingChanged: setSorting,
            }}
            tableClasses="bg-transparent border-0"
            thClasses="border-b border-gray-600 bg-transparent text-gray-400 text-xs font-normal uppercase tracking-wider px-4 py-2"
            stylingOptions={{
              alternateLightRows: false,
              hoverRowColor: 'gray',
            }}
            compact={true}
          />
        </div>
      </Popover>
    </>
  );
};

export default PoolsColumn;
